import { featureRefKey, type SelectableFeatureRef } from './selectable-feature-ref.js'

export type SelectionChangeListener = (selected: ReadonlyMap<string, SelectableFeatureRef>) => void

class SelectionService {
  private selected = new Map<string, SelectableFeatureRef>()
  private listeners: SelectionChangeListener[] = []

  select(ref: SelectableFeatureRef): void {
    const key = featureRefKey(ref)
    if (this.selected.has(key)) return
    this.selected.set(key, ref)
    this.notify()
  }

  /** Add many refs with a single notification — box-select must not fire one
   *  change (and one full re-highlight) per feature. */
  selectMany(refs: SelectableFeatureRef[]): void {
    let added = false
    for (const ref of refs) {
      const key = featureRefKey(ref)
      if (this.selected.has(key)) continue
      this.selected.set(key, ref)
      added = true
    }
    if (added) this.notify()
  }

  deselect(ref: SelectableFeatureRef): void {
    const key = featureRefKey(ref)
    if (!this.selected.has(key)) return
    this.selected.delete(key)
    this.notify()
  }

  toggle(ref: SelectableFeatureRef): void {
    const key = featureRefKey(ref)
    if (this.selected.has(key)) {
      this.selected.delete(key)
    } else {
      this.selected.set(key, ref)
    }
    this.notify()
  }

  replaceSelection(refs: SelectableFeatureRef[]): void {
    this.selected.clear()
    for (const ref of refs) {
      this.selected.set(featureRefKey(ref), ref)
    }
    this.notify()
  }

  clearAll(): void {
    if (this.selected.size === 0) return
    this.selected.clear()
    this.notify()
  }

  clearBySource(source: string): void {
    let changed = false
    for (const [key, ref] of this.selected) {
      if (ref.source === source) {
        this.selected.delete(key)
        changed = true
      }
    }
    if (changed) this.notify()
  }

  isSelected(ref: SelectableFeatureRef): boolean {
    return this.selected.has(featureRefKey(ref))
  }

  getSelected(): ReadonlyMap<string, SelectableFeatureRef> {
    return this.selected
  }

  getSelectedBySource(source: string): SelectableFeatureRef[] {
    return [...this.selected.values()].filter(r => r.source === source)
  }

  getSelectedUserFeatureIds(): string[] {
    return this.getSelectedBySource('user').map(
      r => (r as { source: 'user'; featureId: string }).featureId
    )
  }

  get count(): number {
    return this.selected.size
  }

  get userFeatureCount(): number {
    return this.getSelectedBySource('user').length
  }

  get overlayFeatureCount(): number {
    return this.count - this.userFeatureCount
  }

  onChange(listener: SelectionChangeListener): () => void {
    this.listeners.push(listener)
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener)
    }
  }

  private notify(): void {
    for (const listener of this.listeners) {
      listener(this.selected)
    }
  }
}

export const selectionService = new SelectionService()
